"use client";

// Built on Base UI's unstyled Button — see full API at:
// https://base-ui.com/react/components/button

import { Button as BaseButton } from "@base-ui/react/button";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsCTAClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { stripPiiParams } from "@marketing-sites/utils/strip-pii-params";
import { isCrossAppHref } from "@marketing-sites/utils/cross-app-href";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";
import type { VariantProps } from "tailwind-variants";
import { Link, type LinkProps } from "../link";
import { cn } from "@marketing-sites/utils/cn";
import { buttonVariants } from "./variants";

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export type LinkButtonProps = BaseButton.Props &
  Pick<LinkProps, "href" | "prefetch" | "replace" | "scroll"> &
  ButtonVariantProps & {
    analyticsLabel?: string;
    analyticsId: string;
    /**
     * Render a real anchor instead of `next/link`, for a destination the
     * router must not SPA-intercept (an edge handoff that redirects, or a
     * click the URL-param forwarder has to see). Prefer this over building the
     * anchor in `render` — the localizer never sees a `render` href.
     */
    hardNavigate?: boolean;
  };

type PlainButtonProps = BaseButton.Props &
  ButtonVariantProps & {
    href?: never;
    prefetch?: never;
    replace?: never;
    scroll?: never;
    hardNavigate?: never;
    analyticsLabel?: string;
    analyticsId: string;
  };

export type ButtonProps = LinkButtonProps | PlainButtonProps;

export function Button({
  href,
  prefetch,
  replace,
  scroll,
  hardNavigate,
  render,
  onClick,
  children,
  className,
  variant,
  "aria-label": ariaLabel,
  analyticsLabel,
  analyticsId,
  ...rest
}: ButtonProps) {
  const localizeHref = useLocalizeHref();
  // Checked before localising: a prefixed path no longer matches CROSS_APP_PATHS.
  const isCrossApp = typeof href === "string" && isCrossAppHref(href);
  const localizedHref = typeof href === "string" ? localizeHref(href) : href;

  const handleClick: typeof onClick = (event) => {
    const location = getAnalyticsRegion(event.currentTarget) ?? "Unknown";
    const text =
      analyticsLabel ??
      ariaLabel ??
      (typeof children === "string" ? children : "Button");

    if (href) {
      triggerAnalyticsCTAClick({
        text,
        location,
        destination: stripPiiParams(
          typeof localizedHref === "string"
            ? localizedHref
            : String(localizedHref),
        ),
        analyticsId,
      });
    } else {
      triggerAnalyticsCTAClick({ text, location, analyticsId });
    }

    onClick?.(event);
  };

  return (
    <BaseButton
      className={cn(buttonVariants({ variant }), className)}
      render={
        render ??
        (localizedHref ? (
          typeof localizedHref === "string" && (isCrossApp || hardNavigate) ? (
            // Separate app on the same origin — hard-navigate so next/link
            // doesn't SPA-intercept and the URL forwarder can act on the click.
            <a href={localizedHref} />
          ) : (
            <Link
              href={localizedHref}
              prefetch={prefetch}
              replace={replace}
              scroll={scroll}
              disableAnalytics
            />
          )
        ) : undefined)
      }
      nativeButton={!href}
      aria-label={ariaLabel}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </BaseButton>
  );
}
